/**
 * Device Fingerprinting Utility
 * Creates a unique fingerprint for each device to detect session hijacking
 */

/**
 * Generate device fingerprint
 * Combines multiple browser/device characteristics
 */
export const generateDeviceFingerprint = () => {
  const components = []

  // Screen resolution
  components.push(`screen:${screen.width}x${screen.height}`)
  components.push(`avail:${screen.availWidth}x${screen.availHeight}`)
  components.push(`color:${screen.colorDepth}`)

  // Timezone
  components.push(`tz:${Intl.DateTimeFormat().resolvedOptions().timeZone}`)
  components.push(`tzOffset:${new Date().getTimezoneOffset()}`)

  // Language
  components.push(`lang:${navigator.language}`)
  components.push(`langs:${navigator.languages?.join(',') || ''}`)

  // Platform
  components.push(`platform:${navigator.platform}`)
  components.push(`vendor:${navigator.vendor}`)

  // Hardware
  if (navigator.hardwareConcurrency) {
    components.push(`cores:${navigator.hardwareConcurrency}`)
  }
  if (navigator.deviceMemory) {
    components.push(`memory:${navigator.deviceMemory}`)
  }

  // Canvas fingerprint (more unique)
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    ctx.textBaseline = 'top'
    ctx.font = '14px Arial'
    ctx.fillText('Device fingerprint', 2, 2)
    const canvasHash = canvas.toDataURL().slice(0, 50)
    components.push(`canvas:${canvasHash}`)
  } catch (e) {
    // Canvas not available
  }

  // WebGL fingerprint
  try {
    const gl = document.createElement('canvas').getContext('webgl')
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
      if (debugInfo) {
        components.push(`glVendor:${gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)}`)
        components.push(`glRenderer:${gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)}`)
      }
    }
  } catch (e) {
    // WebGL not available
  }

  // Combine and hash
  const fingerprintString = components.join('|')
  return hashString(fingerprintString)
}

/**
 * Simple hash function (for client-side use)
 * For production, consider using a more robust hashing library
 */
const hashString = (str) => {
  let hash = 0
  if (str.length === 0) return hash.toString()
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36)
}

/**
 * Get device information object
 */
export const getDeviceInfo = () => {
  return {
    fingerprint: generateDeviceFingerprint(),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    languages: navigator.languages || [],
    screenWidth: screen.width,
    screenHeight: screen.height,
    colorDepth: screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    hardwareConcurrency: navigator.hardwareConcurrency || null,
    deviceMemory: navigator.deviceMemory || null,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
  }
}

/**
 * Compare device fingerprints
 */
export const compareDeviceFingerprints = (fingerprint1, fingerprint2) => {
  return fingerprint1 === fingerprint2
}

/**
 * Get IP address (client-side approximation)
 * Note: Real IP should be obtained server-side
 */
export const getClientIP = async () => {
  try {
    // Try to get IP from a service (for development/testing)
    // In production, IP should come from server-side
    // Note: This may be blocked by ad blockers - that's okay, we'll continue without it
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000) // 3 second timeout
    
    try {
      const response = await fetch('https://api.ipify.org?format=json', {
        signal: controller.signal,
      })
      
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        return null
      }
      
      const data = await response.json()
      return data.ip
    } catch (fetchError) {
      clearTimeout(timeoutId)
      // Silently fail - IP fetching is optional and often blocked by ad blockers or browser extensions
      return null
    }
  } catch (error) {
    // Silently fail - IP fetching is optional and often blocked by ad blockers
    // This is expected behavior and not an error
    return null
  }
}

/**
 * Store device fingerprint in session storage
 */
export const storeDeviceFingerprint = () => {
  const fingerprint = generateDeviceFingerprint()
  const deviceInfo = getDeviceInfo()
  
  try {
    sessionStorage.setItem('device_fingerprint', fingerprint)
    sessionStorage.setItem('device_info', JSON.stringify(deviceInfo))
    return fingerprint
  } catch (error) {
    console.error('Failed to store device fingerprint:', error)
    return null
  }
}

/**
 * Retrieve stored device fingerprint
 */
export const getStoredDeviceFingerprint = () => {
  try {
    return sessionStorage.getItem('device_fingerprint')
  } catch (error) {
    console.error('Failed to retrieve device fingerprint:', error)
    return null
  }
}

/**
 * Verify device fingerprint matches stored one
 */
export const verifyDeviceFingerprint = () => {
  const stored = getStoredDeviceFingerprint()
  const current = generateDeviceFingerprint()
  
  if (!stored) {
    // First time, store it
    storeDeviceFingerprint()
    return { valid: true, isNew: true }
  }
  
  const matches = compareDeviceFingerprints(stored, current)
  return { valid: matches, isNew: false, stored, current }
}

